import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  Flame, 
  Camera, 
  Users, 
  TrendingUp, 
  MapPin, 
  Clock,
  AlertTriangle,
  Upload
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();

  // Fetch dashboard data
  const { data: fires, isLoading: firesLoading } = useQuery(
    'dashboard-fires',
    () => apiService.getFires({ active_only: true, limit: 5 }),
    { refetchInterval: 30000 } // Refresh every 30 seconds
  );

  const { data: photos, isLoading: photosLoading } = useQuery(
    'dashboard-photos',
    () => apiService.getPhotos({ limit: 6 }),
    { refetchInterval: 60000 } // Refresh every minute
  );

  const { data: myPhotos, isLoading: myPhotosLoading } = useQuery(
    'dashboard-my-photos',
    () => apiService.getMyPhotos({ limit: 3 })
  );

  const { data: fireStats, isLoading: statsLoading } = useQuery(
    'fire-stats',
    () => apiService.getFireStats(),
    { enabled: isAdmin(), refetchInterval: 60000 }
  );

  const { data: userStats, isLoading: userStatsLoading } = useQuery(
    'user-stats',
    () => apiService.getUserStats(),
    { enabled: isAdmin(), refetchInterval: 60000 }
  );

  const StatCard = ({ title, value, icon: Icon, color = 'primary', link }) => {
    const colorClasses = {
      primary: 'bg-primary-500',
      fire: 'bg-fire-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
    };

    const CardContent = () => (
      <div className="card p-6">
        <div className="flex items-center">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {value}
            </p>
          </div>
        </div>
      </div>
    );

    return link ? (
      <Link to={link} className="block hover:scale-105 transition-transform">
        <CardContent />
      </Link>
    ) : (
      <CardContent />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Bem-vindo, {user?.name}! Aqui está o resumo da atividade.
          </p>
        </div>
        <Link
          to="/upload"
          className="btn-fire flex items-center space-x-2"
        >
          <Upload className="w-5 h-5" />
          <span>Enviar Foto</span>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Incêndios Ativos"
          value={fires?.total || 0}
          icon={Flame}
          color="fire"
          link="/fires"
        />
        <StatCard
          title="Total de Fotos"
          value={photos?.total || 0}
          icon={Camera}
          color="blue"
          link="/photos"
        />
        <StatCard
          title="Minhas Fotos"
          value={myPhotos?.total || 0}
          icon={Camera}
          color="primary"
          link="/photos?user=me"
        />
        {isAdmin() && (
          <StatCard
            title="Utilizadores"
            value={userStats?.active_users || 0}
            icon={Users}
            color="green"
            link="/admin"
          />
        )}
      </div>

      {/* Fire Status Breakdown */}
      {fireStats?.status_breakdown && fireStats.status_breakdown.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Incêndios por Estado
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {fireStats.status_breakdown.map((status, index) => (
              <Link
                key={index}
                to={`/fires?status=${encodeURIComponent(status.status)}`}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: `#${status.color}` }}
                  ></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {status.status}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  {status.count}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Admin Stats (if admin) */}
      {isAdmin() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Incêndios Recentes"
            value={fireStats?.recent_fires || 0}
            icon={TrendingUp}
            color="yellow"
          />
          <StatCard
            title="Fotos Recentes"
            value={userStats?.recent_photos || 0}
            icon={TrendingUp}
            color="blue"
          />
          <StatCard
            title="Novos Utilizadores"
            value={userStats?.recent_users || 0}
            icon={Users}
            color="green"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Active Fires */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Incêndios Ativos Recentes
            </h2>
            <Link
              to="/fires"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium"
            >
              Ver todos
            </Link>
          </div>
          
          {firesLoading ? (
            <LoadingSpinner />
          ) : fires?.fires?.length > 0 ? (
            <div className="space-y-3">
              {fires.fires.slice(0, 5).map((fire) => (
                <Link
                  key={fire.id}
                  to={`/fires/${fire.id}`}
                  className="block p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-fire-500 rounded-full"></div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {fire.location}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {fire.district} • {fire.photo_count} fotos
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {fire.date_str}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Nenhum incêndio ativo encontrado
            </p>
          )}
        </div>

        {/* Recent Photos */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Fotos Recentes
            </h2>
            <Link
              to="/photos"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium"
            >
              Ver galeria
            </Link>
          </div>
          
          {photosLoading ? (
            <LoadingSpinner />
          ) : photos?.photos?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {photos.photos.slice(0, 4).map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={apiService.getPhotoUrl(photo.id)}
                    alt={`Foto de ${photo.fire?.name}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute bottom-1 left-1 right-1">
                    <p className="text-xs text-white bg-black bg-opacity-50 rounded px-2 py-1 truncate">
                      {photo.fire?.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Nenhuma foto encontrada
            </p>
          )}
        </div>
      </div>

      {/* My Recent Photos */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            As Minhas Fotos Recentes
          </h2>
          <Link
            to="/photos?user=me"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium"
          >
            Ver todas
          </Link>
        </div>
        
        {myPhotosLoading ? (
          <LoadingSpinner />
        ) : myPhotos?.photos?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {myPhotos.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={apiService.getPhotoUrl(photo.id)}
                  alt={`Foto de ${photo.fire?.name}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg"></div>
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-sm text-white bg-black bg-opacity-50 rounded px-2 py-1 truncate">
                    {photo.fire?.location}
                  </p>
                  <p className="text-xs text-gray-300 mt-1">
                    {new Date(photo.uploaded_at).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Ainda não enviou nenhuma foto
            </p>
            <Link to="/upload" className="btn-primary">
              Enviar primeira foto
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
